import { useTranslation } from 'react-i18next'
import { formatScheduleText } from '../../utils/schedule-helpers'

interface SnapshotMedication {
  name: string
  timeSlot: string
  specificTime?: string
  notes?: string
  frequency?: string
  frequencyDayOfWeek?: number
  frequencyOrdinal?: string
  frequencyDayOfMonth?: number
  oneTimeDate?: string
  remindDayBefore?: boolean
}

interface Snapshot {
  _id: string
  timestamp: number
  changeDescription: string
  changeType: string
  medications: SnapshotMedication[]
}

interface SnapshotDetailProps {
  snapshot: Snapshot
  onBack: () => void
}

export function SnapshotDetail({ snapshot, onBack }: SnapshotDetailProps) {
  const { t } = useTranslation()

  const getTimeSlotLabel = (slot: string): string => {
    return t(`medicationManagement.timeSlots.${slot}`, slot)
  }

  const formatDate = (timestamp: number): string => {
    return new Date(timestamp).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const containerStyles: React.CSSProperties = {
    padding: '0',
  }

  const headerStyles: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    marginBottom: '1rem',
  }

  const backButtonStyles: React.CSSProperties = {
    minWidth: '44px',
    minHeight: '44px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    backgroundColor: '#fff',
    cursor: 'pointer',
    fontSize: '18px',
    padding: 0,
  }

  const titleStyles: React.CSSProperties = {
    fontSize: '18px',
    fontWeight: 600,
    color: '#1a1a1a',
    margin: 0,
  }

  const subtitleStyles: React.CSSProperties = {
    fontSize: '14px',
    color: '#6b7280',
    marginBottom: '1.5rem',
  }

  const cardListStyles: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
  }

  const medCardStyles: React.CSSProperties = {
    backgroundColor: '#fff',
    borderRadius: '8px',
    padding: '1rem',
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
    border: '1px solid #f3f4f6',
  }

  const medNameStyles: React.CSSProperties = {
    fontSize: '16px',
    fontWeight: 500,
    color: '#1a1a1a',
    marginBottom: '0.25rem',
  }

  const medDetailStyles: React.CSSProperties = {
    fontSize: '14px',
    color: '#6b7280',
  }

  const scheduleTextStyles: React.CSSProperties = {
    fontSize: '13px',
    color: '#2563eb',
    marginTop: '0.25rem',
  }

  const notesTextStyles: React.CSSProperties = {
    fontSize: '13px',
    color: '#9ca3af',
    fontStyle: 'italic',
    marginTop: '0.25rem',
  }

  const emptyStyles: React.CSSProperties = {
    textAlign: 'center',
    padding: '2rem',
    color: '#9ca3af',
    fontSize: '14px',
  }

  const groupHeaderStyles: React.CSSProperties = {
    fontSize: '14px',
    fontWeight: 600,
    color: '#6b7280',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: '0.5rem',
  }

  // Sort medications chronologically: morning → afternoon → evening → bedtime, then by specificTime
  const timeSlotOrder: Record<string, number> = { morning: 0, afternoon: 1, evening: 2, bedtime: 3 }
  const toMinutes = (timeStr: string) => {
    const m = timeStr.match(/^(\d{1,2}):(\d{2})\s?(AM|PM)$/i)
    if (!m) return 0
    let h = parseInt(m[1], 10)
    const min = parseInt(m[2], 10)
    const pm = m[3].toUpperCase() === 'PM'
    if (h === 12) h = 0
    return (pm ? h + 12 : h) * 60 + min
  }
  const sortMeds = (meds: SnapshotMedication[]) =>
    [...meds].sort((a, b) => {
      const slotDiff = (timeSlotOrder[a.timeSlot] ?? 99) - (timeSlotOrder[b.timeSlot] ?? 99)
      if (slotDiff !== 0) return slotDiff
      return toMinutes(a.specificTime ?? '') - toMinutes(b.specificTime ?? '')
    })

  // Group medications by frequency
  const frequencyGroupOrder = [
    'daily',
    'weekly',
    'biweekly',
    'monthly_day_of_week',
    'monthly_date',
    'one_time',
  ]
  const frequencyGroupLabels: Record<string, string> = {
    daily: t('medicationManagement.schedule.daily'),
    weekly: t('medicationManagement.schedule.weekly'),
    biweekly: t('medicationManagement.schedule.biweekly'),
    monthly_day_of_week: t('medicationManagement.schedule.monthlyDayOfWeek'),
    monthly_date: t('medicationManagement.schedule.monthlyDate'),
    one_time: t('medicationManagement.schedule.oneTime'),
  }
  const groupedMedications = frequencyGroupOrder
    .map(freq => ({
      frequency: freq,
      label: frequencyGroupLabels[freq],
      meds: sortMeds(snapshot.medications.filter(m => (m.frequency || 'daily') === freq)),
    }))
    .filter(g => g.meds.length > 0)

  return (
    <div style={containerStyles}>
      <div style={headerStyles}>
        <button
          style={backButtonStyles}
          onClick={onBack}
          aria-label={t('medicationManagement.history.backToTimeline')}
        >
          &larr;
        </button>
        <h2 style={titleStyles}>
          {t('medicationManagement.history.snapshotTitle', {
            date: formatDate(snapshot.timestamp),
          })}
        </h2>
      </div>

      <div style={subtitleStyles}>{snapshot.changeDescription}</div>

      {snapshot.medications.length === 0 ? (
        <div style={emptyStyles}>{t('medicationManagement.noMedications')}</div>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 420px), 1fr))',
            gap: '1.5rem',
          }}
        >
          {groupedMedications.map(group => (
            <div key={group.frequency}>
              <h3 style={groupHeaderStyles}>{group.label}</h3>
              <div style={cardListStyles}>
                {group.meds.map((med, index) => (
                  <div key={index} style={medCardStyles}>
                    <div style={medNameStyles}>{med.name}</div>
                    <div style={medDetailStyles}>
                      {getTimeSlotLabel(med.timeSlot)}
                      {med.specificTime && ` \u2022 ${med.specificTime}`}
                    </div>
                    {med.frequency && med.frequency !== 'daily' && (
                      <div style={scheduleTextStyles}>{formatScheduleText(med)}</div>
                    )}
                    {med.notes && <div style={notesTextStyles}>{med.notes}</div>}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
